/**
 * IPAMDhcp — /networks/dhcp (rangos DHCP y sus reservas)
 *
 * Un **rango (scope)** es el tramo de IPs de una subred que un DHCP reparte
 * dinámicamente, junto con lo que entrega a los clientes: **dominio**,
 * **gateway**, **DNS** y **NTP**; «quién lo sirve» se guarda como referencia a
 * un elemento de la CMDB, para que el Diseñador de Servicios pinte solo la
 * dependencia. Una **reserva** es la excepción: fija una **MAC** a una IP.
 *
 * Esta pantalla era la más distinta del módulo: maestro-detalle (tarjetas de
 * rango a la izquierda, reservas a la derecha), tema oscuro escrito a mano,
 * emojis y sin buscador, filtros, orden ni export.
 *
 * Ahora: `ListPage` lista los **rangos** (buscador, filtros, columnas, orden,
 * export, vistas y los botones `[acciones propias] · Copiar · Editar ·
 * Eliminar`) y al pulsar una fila se abre la **ficha del rango en lectura**, con
 * sus opciones de scope y su tabla de reservas dentro. El alta/edición del scope
 * y la reserva siguen donde estaban, pero en modales del estándar.
 *
 * Ojo: `?subnetId=` se lee aquí porque Subredes enlaza con «Ver rango DHCP»; el
 * componente lo ignoraba y el enlace abría la pantalla sin filtrar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrashIcon,
  PlusIcon,
  PencilSquareIcon,
  ServerStackIcon,
  ListBulletIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import api from '../services/api';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';
import '../styles/DetailView.css';   // `.dv-fieldset` (la ficha se apaga de golpe en lectura)

// ── helpers ──────────────────────────────────────────────────────────────────

const OBJECT_LABELS = {
  workstation: 'Workstation', printer: 'Impresora',
  ip_phone: 'Teléfono IP', server: 'Servidor', other: 'Otro',
};

function normMac(v) {
  return v.replace(/[^0-9a-fA-F]/g, '').slice(0, 12).replace(/(.{2})(?=.)/g, '$1:').toLowerCase();
}

const IPv4_RE = /^(25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3}$/;
function isValidIP(v) { return IPv4_RE.test((v || '').trim()); }

function ip2n(ip) { return ip.split('.').reduce((a, b) => a * 256 + Number(b), 0); }

function rangeSize(s, e) {
  try {
    const d = ip2n(e) - ip2n(s) + 1;
    return d > 0 ? d : null;
  } catch { return null; }
}

const EMPTY_RANGE_FORM = {
  subnet_id: '', start_ip: '', end_ip: '', owner: '', description: '',
  // Opciones del scope DHCP
  domain_name: '', gateway: '', dns_servers: [], ntp_servers: [],
  dhcp_ci_type: '', dhcp_ci_id: '',
};
const EMPTY_RES_FORM   = { mac_address: '', ip: '', hostname: '', description: '', object_type: '', object_id: '' };

// Tipo de elemento → icono, para los chips de "lo sirve" / DNS / NTP
const CI_ICONS = {
  rack_element: '🖥️', virt_vm: '🧊', firewall: '🛡️', router: '🌐', service: '🧩',
  wifi_controller: '📡', wifi_access_point: '📶', workstation: '💻', printer: '🖨️',
  ipam_ip: '🔢', ipam_subnet: '🕸️', ipam_vlan: '🏷️', storage_lun: '💾',
};
const ciIcon = (t) => CI_ICONS[t] || '🔹';

/** Vínculos de CMDB de las opciones (DNS/NTP) → {ip, ci_type, ci_id, ci_name}. */
function opcionesList(values) {
  return (values || []).map((v) => (
    typeof v === 'string' ? { ip: v, ci_type: null, ci_id: null, ci_name: null } : v
  ));
}

/* Tramos de ocupación de un rango: reservas sobre el total de IPs del tramo. */
const OCCUPATION_RANGES = {
  none: { label: 'Sin reservas', test: (p) => p === 0 },
  low:  { label: '< 50 %',       test: (p) => p > 0 && p < 50 },
  mid:  { label: '50 – 80 %',    test: (p) => p >= 50 && p <= 80 },
  high: { label: '> 80 %',       test: (p) => p > 80 },
};

/* ── Selector de elementos de la CMDB (buscador con resultados en vivo) ──────── */
function CiPicker({ companyId, placeholder, onPick, types }) {
  const [term, setTerm]   = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen]   = useState(false);
  const timer = useRef(null);

  const search = (q) => {
    setTerm(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.ciSearch(companyId, { types, q: q.trim(), limit: 12 });
        setItems(res?.results || []);
        setOpen(true);
      } catch { setItems([]); }
    }, 250);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-control form-control-sm"
        placeholder={placeholder}
        value={term}
        onChange={(e) => search(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
      />
      {open && items.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 30, left: 0, right: 0, top: '100%', marginTop: 2,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8,
          maxHeight: 220, overflowY: 'auto', boxShadow: '0 12px 30px rgba(0,0,0,.35)',
        }}>
          {items.map((it) => (
            <button
              key={`${it.ci_type}-${it.ci_id}`}
              type="button"
              className="btn btn-sm w-100 text-start"
              style={{ borderRadius: 0, color: 'var(--text-primary)', fontSize: 12 }}
              onClick={() => { onPick(it); setTerm(''); setItems([]); setOpen(false); }}
            >
              <strong>{it.name}</strong>
              <span style={{ color: 'var(--text-muted)' }}> · {it.ci_type_label || it.ci_type}</span>
              {it.description && <span style={{ color: 'var(--text-muted)' }}> · {it.description}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Chips de un elemento del scope (lo sirve / DNS / NTP) con su ✕. */
function CiChip({ entrada, onRemove }) {
  const nombre = entrada.ci_name || entrada.ip || entrada.ci_type;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 4, marginBottom: 4,
      background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.35)',
      color: '#4f46e5', borderRadius: 999, fontSize: 11, padding: '2px 8px',
    }}>
      {entrada.ci_type ? ciIcon(entrada.ci_type) : '🔢'} {nombre}
      {entrada.ip && entrada.ci_name ? ` (${entrada.ip})` : ''}
      {onRemove && (
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 0, lineHeight: 1 }}>✕</button>
      )}
    </span>
  );
}

// ── sub-components ────────────────────────────────────────────────────────────

function StatBar({ stats, loading }) {
  const pct = stats?.usage_pct;
  const items = [
    { label: 'Rangos DHCP',       value: stats?.dhcp_ranges ?? '—' },
    { label: 'IPs en rangos',     value: stats?.total_range_ips ?? '—' },
    { label: 'Reservas activas',  value: stats?.reservations ?? '—' },
    { label: 'Subredes con DHCP', value: stats?.subnets_with_dhcp ?? '—' },
    {
      label: 'Uso de rangos',
      value: pct != null ? `${pct}%` : '—',
      tone: pct == null ? undefined : pct >= 90 ? 'var(--red-600)' : pct >= 70 ? 'var(--amber-600)' : 'var(--green-600)',
    },
  ];
  return (
    <div className="list-kpis list-kpis--chips" style={{ opacity: loading ? 0.5 : 1 }}>
      {items.map(it => (
        <div key={it.label} className="list-kpi-chip" style={{ cursor: 'default' }}>
          <span className="list-kpi-value" style={it.tone ? { color: it.tone } : undefined}>{it.value}</span>
          <span className="list-kpi-label">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── SubnetIpMapPicker ─────────────────────────────────────────────────────────
// Compact interactive IP grid used inside the DHCP range form.
// • Purple  = already in a DHCP range (non-clickable)
// • Green   = free / available
// • Red/Org = assigned / reserved (non-clickable)
// • Blue outline "S" marker = selected start_ip
// • Blue outline "E" marker = selected end_ip
//
// Click rules:
//   - If no start selected → sets start
//   - If start set, no end → sets end (must be ≥ start; swaps if needed)
//   - Click on start → deselect start (and end)
//   - Click on end   → deselect end
function SubnetIpMapPicker({ companyId, subnetId, startIp, endIp, onStartChange, onEndChange }) {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compact, setCompact] = useState(true);

  useEffect(() => {
    if (!subnetId || !companyId) { setMapData(null); return; }
    let cancelled = false;
    setLoading(true);
    api.getIpamSubnetMap(Number(subnetId), companyId, 512)
      .then(r => { if (!cancelled) setMapData(r.data || null); })
      .catch(() => { if (!cancelled) setMapData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subnetId, companyId]);

  const ips = mapData?.generated_ips || [];

  const handleClick = (entry) => {
    const ip = entry.ip;
    if (entry.in_dhcp_range) return;
    if (entry.status === 'assigned' || entry.status === 'conflict') return;

    // deselect
    if (ip === startIp) { onStartChange(''); onEndChange(''); return; }
    if (ip === endIp)   { onEndChange(''); return; }

    if (!startIp) {
      onStartChange(ip);
    } else if (!endIp) {
      // swap if needed
      if (ip2n(ip) < ip2n(startIp)) {
        onEndChange(startIp);
        onStartChange(ip);
      } else {
        onEndChange(ip);
      }
    } else {
      // re-start selection
      onStartChange(ip);
      onEndChange('');
    }
  };

  if (!subnetId) return null;

  const startN = isValidIP(startIp) ? ip2n(startIp) : null;
  const endN   = isValidIP(endIp)   ? ip2n(endIp)   : null;

  function badgeStyle(entry) {
    const n = ip2n(entry.ip);
    const isStart = entry.ip === startIp;
    const isEnd   = entry.ip === endIp;
    const inSel   = startN !== null && endN !== null && n >= startN && n <= endN;

    if (entry.in_dhcp_range) return {
      background: 'rgba(99,102,241,.2)', color: '#a5b4fc', border: '2px solid rgba(99,102,241,.4)', cursor: 'not-allowed',
    };
    if (entry.is_gateway) return {
      background: '#0891b2', color: '#fff', border: '2px solid #0e7490', cursor: 'not-allowed',
    };
    if (entry.status === 'assigned') return {
      background: '#ef4444', color: '#fff', border: '2px solid #dc2626', cursor: 'not-allowed',
    };
    if (entry.status === 'reserved') return {
      background: '#f59e0b', color: '#fff', border: '2px solid #d97706', cursor: 'not-allowed',
    };
    // free
    const base = {
      background: isStart || isEnd ? '#4338ca' : inSel ? 'rgba(99,102,241,.25)' : 'rgba(16,185,129,.15)',
      color:      isStart || isEnd ? '#fff'    : inSel ? '#a5b4fc' : '#6ee7b7',
      border:     `2px solid ${isStart || isEnd ? '#6366f1' : inSel ? 'rgba(99,102,241,.5)' : 'rgba(16,185,129,.4)'}`,
      cursor: 'pointer',
    };
    return base;
  }

  function label(entry) {
    const last = entry.ip.split('.').pop();
    if (entry.ip === startIp) return compact ? `S` : `S:${last}`;
    if (entry.ip === endIp)   return compact ? `E` : `E:${last}`;
    return compact ? `.${last}` : entry.ip;
  }

  // Group by /24
  const groups = [];
  const groupMap = new Map();
  for (const e of ips) {
    const prefix = e.ip.split('.').slice(0, 3).join('.');
    if (!groupMap.has(prefix)) { groupMap.set(prefix, []); groups.push(prefix); }
    groupMap.get(prefix).push(e);
  }

  const dhcpCount = ips.filter(e => e.in_dhcp_range).length;
  const freeCount = ips.filter(e => !e.in_dhcp_range && e.status === 'free').length;

  return (
    <div style={{ marginBottom: 10 }}>
      {loading && (
        <div className="text-center text-muted py-2" style={{ fontSize: 12 }}>
          <span className="spinner-border spinner-border-sm me-1" />Cargando mapa…
        </div>
      )}
      {!loading && ips.length > 0 && (
        <>
          {/* legend + controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>
              {freeCount} libre{freeCount !== 1 ? 's' : ''}{dhcpCount > 0 ? ` · ${dhcpCount} en rango DHCP` : ''}
              {mapData?.truncated ? ' · (parcial)' : ''}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {[
                { bg: 'rgba(16,185,129,.2)',  color: '#6ee7b7', label: 'Libre' },
                { bg: 'rgba(99,102,241,.25)', color: '#a5b4fc', label: 'DHCP' },
                { bg: '#ef4444', color: '#fff',    label: 'Asig.' },
                { bg: '#4338ca', color: '#fff',    label: 'S/E' },
              ].map(l => (
                <span key={l.label} style={{
                  background: l.bg, color: l.color, borderRadius: 4,
                  padding: '1px 5px', fontSize: 10, fontWeight: 600, border: '1px solid rgba(0,0,0,.1)',
                }}>{l.label}</span>
              ))}
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
                <input type="checkbox" checked={compact} onChange={e => setCompact(e.target.checked)} style={{ width: 12 }} />
                Compact
              </label>
            </div>
          </div>
          {/* instruction */}
          <div style={{ fontSize: 11, color: '#818cf8', marginBottom: 4 }}>
            {!startIp ? '① Haz clic en la IP de inicio' :
             !endIp   ? '② Haz clic en la IP de fin' :
                        `Rango: ${startIp} → ${endIp} · ${(rangeSize(startIp, endIp) ?? 0).toLocaleString()} IPs`}
          </div>
          {/* grid */}
          <div style={{
            border: '1px solid rgba(255,255,255,.07)', borderRadius: 6, padding: 6,
            maxHeight: 220, overflowY: 'auto', background: 'rgba(255,255,255,.03)',
          }}>
            {groups.map(prefix => (
              <div key={prefix}>
                {groups.length > 1 && (
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3, marginTop: 4 }}>{prefix}.0/24</div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 2 : 3 }}>
                  {groupMap.get(prefix).map(entry => (
                    <button
                      key={entry.ip}
                      type="button"
                      title={[
                        entry.ip,
                        entry.in_dhcp_range ? '📡 En rango DHCP' : null,
                        entry.is_gateway    ? '★ Gateway' : null,
                        entry.status !== 'free' && !entry.in_dhcp_range ? `Estado: ${entry.status}` : null,
                      ].filter(Boolean).join(' · ')}
                      onClick={() => handleClick(entry)}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: compact ? 10 : 11,
                        padding: compact ? '1px 3px' : '2px 5px',
                        borderRadius: 3,
                        fontWeight: (entry.ip === startIp || entry.ip === endIp) ? 700 : 400,
                        transition: 'all .08s',
                        minWidth: compact ? 28 : 80,
                        textAlign: 'center',
                        ...badgeStyle(entry),
                      }}
                    >
                      {label(entry)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {!loading && ips.length === 0 && subnetId && (
        <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '8px 0' }}>
          Sin IPs en esta subred
        </div>
      )}
    </div>
  );
}



export default function IPAMDhcp({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initSubnet = searchParams.get('subnetId') || '';

  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const errTimer = useRef(null);
  const [success, setSuccess]       = useState('');

  const [stats, setStats]           = useState(null);
  const [subnets, setSubnets]       = useState([]);
  const [ranges, setRanges]         = useState([]);        // only type=dhcp
  const [reservations, setReservations] = useState([]);    // for selectedRange
  const [selectedRange, setSelectedRange] = useState(null);   // ficha abierta

  /* Filtros */
  const [search, setSearch]             = useState('');
  const [filterSubnet, setFilterSubnet] = useState(initSubnet);
  const [filterSite, setFilterSite]     = useState('');
  const [filterServed, setFilterServed] = useState('');
  const [filterRes, setFilterRes]       = useState('');
  const [filterOccupation, setFilterOccupation] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [hlId, setHlId]                 = useState(null);
  const [knownSites, setKnownSites]     = useState([]);

  /* Confirmaciones: un solo diálogo del estándar (antes `window.confirm`). */
  const [confirmDelete, setConfirmDelete] = useState(null);   // { kind, item }

  // Devices for optional linking
  const [devices, setDevices]       = useState({ workstation: [], printer: [], ip_phone: [] });

  // Forms
  const [showRangeForm, setShowRangeForm] = useState(false);
  const [rangeForm, setRangeForm]   = useState(EMPTY_RANGE_FORM);
  const [editingRange, setEditingRange] = useState(null);   // null = nuevo scope
  const [savingRange, setSavingRange] = useState(false);

  const [showResForm, setShowResForm] = useState(false);
  const [resForm, setResForm]       = useState(EMPTY_RES_FORM);
  const [editingRes, setEditingRes] = useState(null);
  const [savingRes, setSavingRes]   = useState(false);

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  // ── loaders ──────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    if (!companyId) return;
    try {
      const s = await api.getDhcpStats(companyId);
      setStats(s.data);
    } catch { /* non-critical */ }
  }, [companyId]);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [sn, rng, st, ws, pr, ph] = await Promise.all([
        api.getIpamSubnets(companyId),
        api.getIpamRanges(companyId, { type: 'dhcp' }).catch(() => ({ data: [] })),
        api.getDhcpStats(companyId).catch(() => ({ data: null })),
        api.getWorkstations({ company_id: companyId }).catch(() => ({ data: [] })),
        api.getPrinters({ company_id: companyId }).catch(() => ({ data: [] })),
        api.getIPPhones(companyId).catch(() => ({ data: [] })),
      ]);
      setSubnets(sn.data || []);
      setStats(st.data);

      // Enrich ranges with reservation counts and size
      const rawRanges = (rng.data || []).filter(r => r.type === 'dhcp');
      // Fetch reservations count per range: get all + group
      let allResList = [];
      try {
        const allRes = await api.getDhcpReservations(companyId);
        allResList = allRes.data || [];
      } catch { /* ignore */ }

      const resByRange = {};
      allResList.forEach(r => {
        if (!resByRange[r.range_id]) resByRange[r.range_id] = 0;
        resByRange[r.range_id]++;
      });

      const enriched = rawRanges.map(r => {
        let size = 0;
        try {
          const ip2n = ip => ip.split('.').reduce((a, b) => a * 256 + Number(b), 0);
          size = ip2n(r.end_ip) - ip2n(r.start_ip) + 1;
        } catch { /* ignore */ }
        return { ...r, _range_size: size, _reservations_count: resByRange[r.id] || 0 };
      });
      setRanges(enriched);
      setDevices({
        workstation: ws.data || [],
        printer: pr.data || [],
        ip_phone: ph.data || [],
      });
    } catch (e) {
      setError(e.message || 'Error cargando DHCP');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadReservations = useCallback(async (range) => {
    if (!companyId || !range) { setReservations([]); return; }
    try {
      const r = await api.getDhcpReservations(companyId, { range_id: range.id });
      setReservations(r.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando reservas');
    }
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadReservations(selectedRange); }, [selectedRange, loadReservations]);

  /* ── subnet / site helpers ──────────────────────────────────────────────── */
  const subnetById = useMemo(() => {
    const map = {};
    subnets.forEach(s => { map[String(s.id)] = s; });
    return map;
  }, [subnets]);

  const siteOf = useCallback(
    (range) => (range?.subnet_id != null ? (subnetById[String(range.subnet_id)]?.site || '') : ''),
    [subnetById],
  );

  useEffect(() => {
    const sites = new Set();
    ranges.forEach(r => { const s = siteOf(r); if (s) sites.add(s); });
    if (sites.size) {
      setKnownSites(prev => [...new Set([...prev, ...sites])].sort((a, b) => compareText(a, b)));
    }
  }, [ranges, siteOf]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB. */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !ranges.length) return undefined;
    const id = Number(raw);
    const target = ranges.find(r => r.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setSelectedRange(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`dhcp-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, ranges, setSearchParams]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 9000);
    return () => clearTimeout(timer);
  }, [success]);

  /* ── rango: abrir ficha, alta y edición ─────────────────────────────────── */
  const openDetail = useCallback((range, { reservations: focus } = {}) => {
    setSelectedRange(range);
    if (focus) {
      setTimeout(() => {
        const el = document.getElementById('dhcp-reservations');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  }, []);

  const openNewRes = () => {
    setEditingRes(null);
    setResForm(EMPTY_RES_FORM);
    setShowResForm(true);
  };

  const openNewRange = () => {
    setEditingRange(null);
    setRangeForm(EMPTY_RANGE_FORM);
    setShowRangeForm(true);
  };

  /** Copiar rango: se precarga todo menos el tramo de IPs (no puede repetirse). */
  const openCopyRange = (range) => {
    if (!range) return;
    setEditingRange(null);
    setRangeForm({
      ...EMPTY_RANGE_FORM,
      subnet_id: range.subnet_id || '',
      owner: range.owner || '',
      description: range.description || '',
      domain_name: range.domain_name || '',
      gateway: range.gateway || '',
      dns_servers: opcionesList(range.dns_servers),
      ntp_servers: opcionesList(range.ntp_servers),
      dhcp_ci_type: range.dhcp_ci_type || '',
      dhcp_ci_id: range.dhcp_ci_id || '',
    });
    setShowRangeForm(true);
  };

  // ── range CRUD ────────────────────────────────────────────────────────────

  const handleSaveRange = async () => {
    if (!rangeForm.start_ip || !rangeForm.end_ip) return setError('start_ip y end_ip son obligatorios');
    setSavingRange(true);
    try {
      const payload = {
        ...rangeForm,
        type: 'dhcp',
        subnet_id: rangeForm.subnet_id || null,
        dhcp_ci_type: rangeForm.dhcp_ci_type || null,
        dhcp_ci_id: rangeForm.dhcp_ci_id || null,
        dns_servers: opcionesList(rangeForm.dns_servers),
        ntp_servers: opcionesList(rangeForm.ntp_servers),
      };
      if (editingRange) {
        await api.updateIpamRange(editingRange.id, payload, companyId);
        setSuccess(`Rango ${payload.start_ip}–${payload.end_ip} actualizado.`);
      } else {
        await api.createIpamRange(payload, companyId);
        setSuccess(`Rango ${payload.start_ip}–${payload.end_ip} creado.`);
      }
      setShowRangeForm(false);
      setEditingRange(null);
      setRangeForm(EMPTY_RANGE_FORM);
      await loadAll();
      loadStats();
    } catch (e) {
      setError(e.message || 'Error guardando el scope');
    } finally {
      setSavingRange(false);
    }
  };

  // Abre el formulario con los datos del scope para editar sus opciones DHCP.
  const openEditRange = (range) => {
    setEditingRange(range);
    setRangeForm({
      subnet_id: range.subnet_id || '',
      start_ip: range.start_ip || '',
      end_ip: range.end_ip || '',
      owner: range.owner || '',
      description: range.description || '',
      domain_name: range.domain_name || '',
      gateway: range.gateway || '',
      dns_servers: opcionesList(range.dns_servers),
      ntp_servers: opcionesList(range.ntp_servers),
      dhcp_ci_type: range.dhcp_ci_type || '',
      dhcp_ci_id: range.dhcp_ci_id || '',
    });
    setShowRangeForm(true);
  };

  /** Añade/quita un elemento de CMDB en el sitio que otorga el DHCP. */
  const setDhcpCi = (it) => setRangeForm(f => ({
    ...f, dhcp_ci_type: it.ci_type, dhcp_ci_id: it.ci_id, owner: it.name || f.owner,
  }));

  /** Añade una IP suelta o un elemento a las opciones DNS/NTP. */
  const addOption = (campo, entrada) => setRangeForm(f => ({
    ...f, [campo]: [...opcionesList(f[campo]), entrada],
  }));
  const removeOption = (campo, idx) => setRangeForm(f => ({
    ...f, [campo]: opcionesList(f[campo]).filter((_, i) => i !== idx),
  }));

  const addIpOption = (campo, texto) => {
    const limpias = (texto || '').split(',').map(s => s.trim()).filter(Boolean);
    if (!limpias.length) return;
    setRangeForm(f => ({
      ...f,
      [campo]: [
        ...opcionesList(f[campo]),
        ...limpias
          .filter(ip => !opcionesList(f[campo]).some(o => o.ip === ip))
          .map(ip => ({ ip, ci_type: null, ci_id: null, ci_name: null })),
      ],
    }));
  };

  /** Al enlazar un elemento como DNS/NTP se hereda su IP si la tiene. */
  const addCiOption = (campo, it) => addOption(campo, {
    ip: it.description && isValidIP(String(it.description).split(' · ')[0])
      ? String(it.description).split(' · ')[0]
      : null,
    ci_type: it.ci_type,
    ci_id: it.ci_id,
    ci_name: it.name,
  });

  const handleDeleteRange = async (range) => {
    setConfirmDelete(null);
    try {
      await api.deleteIpamRange(range.id, companyId);
      if (selectedRange?.id === range.id) { setSelectedRange(null); setReservations([]); }
      setSuccess(`Rango ${range.start_ip}–${range.end_ip} eliminado con sus reservas.`);
      await loadAll();
      loadStats();
    } catch (e) {
      setError(e.message || 'Error eliminando rango');
    }
  };

  // ── reservation CRUD ──────────────────────────────────────────────────────

  const openEditRes = (res) => {
    setEditingRes(res);
    setResForm({
      mac_address: res.mac_address,
      ip: res.ip || '',
      hostname: res.hostname || '',
      description: res.description || '',
      object_type: res.object_type || '',
      object_id: res.object_id ? String(res.object_id) : '',
    });
    setShowResForm(true);
  };

  const handleSaveRes = async () => {
    if (!resForm.mac_address) return setError('MAC address es obligatoria');
    if (!selectedRange) return setError('Seleccione un rango primero');
    setSavingRes(true);
    try {
      const payload = {
        range_id: selectedRange.id,
        mac_address: resForm.mac_address,
        ip: resForm.ip || undefined,
        hostname: resForm.hostname || undefined,
        description: resForm.description || undefined,
        object_type: resForm.object_type || undefined,
        object_id: resForm.object_id ? Number(resForm.object_id) : undefined,
      };
      if (editingRes) {
        await api.updateDhcpReservation(editingRes.id, payload, companyId);
      } else {
        await api.createDhcpReservation(payload, companyId);
      }
      setShowResForm(false);
      setResForm(EMPTY_RES_FORM);
      setEditingRes(null);
      await loadReservations(selectedRange);
      loadStats();
      loadAll();
    } catch (e) {
      setError(e.message || 'Error guardando reserva');
    } finally {
      setSavingRes(false);
    }
  };

  const handleDeleteRes = async (res) => {
    setConfirmDelete(null);
    try {
      await api.deleteDhcpReservation(res.id, companyId);
      setSuccess(`Reserva de ${res.mac_address} eliminada.`);
      await loadReservations(selectedRange);
      loadStats();
      loadAll();
    } catch (e) {
      setError(e.message || 'Error eliminando reserva');
    }
  };

  // ── derived ───────────────────────────────────────────────────────────────

  const deviceOptions = resForm.object_type ? (devices[resForm.object_type] || []) : [];

  const occupationPct = (range) => {
    const size = range?._range_size || 0;
    if (!size) return 0;
    return Math.min(100, Math.round(((range._reservations_count || 0) / size) * 100));
  };

  /* ── filtrado ────────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const occ = OCCUPATION_RANGES[filterOccupation];
    return ranges.filter((r) => {
      if (filterSubnet && String(r.subnet_id) !== String(filterSubnet)) return false;
      if (filterSite && siteOf(r) !== filterSite) return false;
      const served = Boolean(r.dhcp_ci_id || r.owner);
      if (filterServed === 'yes' && !served) return false;
      if (filterServed === 'no' && served) return false;
      if (filterRes === 'with' && !(r._reservations_count > 0)) return false;
      if (filterRes === 'without' && r._reservations_count > 0) return false;
      if (occ && !occ.test(occupationPct(r))) return false;
      if (filterDomain === 'yes' && !r.domain_name) return false;
      if (filterDomain === 'no' && r.domain_name) return false;
      if (!needle) return true;
      return (r.start_ip || '').includes(needle)
        || (r.end_ip || '').includes(needle)
        || (r.subnet_cidr || '').toLowerCase().includes(needle)
        || (r.domain_name || '').toLowerCase().includes(needle)
        || (r.gateway || '').includes(needle)
        || (r.owner || '').toLowerCase().includes(needle)
        || (r.description || '').toLowerCase().includes(needle)
        || opcionesList(r.dns_servers).some(o => (o.ip || '').includes(needle) || (o.ci_name || '').toLowerCase().includes(needle));
    });
  }, [ranges, search, filterSubnet, filterSite, filterServed, filterRes, filterOccupation,
      filterDomain, siteOf]);

  /* ── columnas (12 de catálogo; 8 visibles de entrada) ───────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    const chips = (list) => opcionesList(list).filter(o => o.ip || o.ci_name);
    return [
      {
        key: 'range',
        label: tr('dhcp.col_range', 'Rango'),
        sortable: true,
        sortValue: (r) => ip2n(r.start_ip || '0.0.0.0'),
        render: (r) => (
          <span className="list-code" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {r.start_ip} – {r.end_ip}
          </span>
        ),
      },
      {
        key: 'size',
        label: tr('dhcp.col_size', 'Tamaño'),
        sortable: true,
        sortValue: (r) => r._range_size || 0,
        render: (r) => <span className="list-hint">{(r._range_size || 0).toLocaleString()} IPs</span>,
      },
      {
        key: 'subnet_cidr',
        label: tr('ipam.subnet', 'Subred'),
        sortable: true,
        sortValue: (r) => r.subnet_cidr || '',
        render: (r) => (r.subnet_cidr
          ? (
            <button
              type="button"
              className="list-code"
              style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0 }}
              title={tr('dhcp.view_subnet', 'Ver la subred del rango')}
              onClick={(event) => { event.stopPropagation(); navigate(`/networks/subnets?cidr=${encodeURIComponent(r.subnet_cidr)}`); }}
            >
              {r.subnet_cidr}
            </button>
          )
          : dash),
      },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (r) => siteOf(r),
        render: (r) => siteOf(r) || dash,
      },
      {
        key: 'served',
        label: tr('dhcp.col_served', 'Lo sirve'),
        sortable: true,
        sortValue: (r) => r.dhcp_ci_name || r.owner || '',
        render: (r) => {
          const served = r.dhcp_ci_name || r.owner;
          if (!served) return <span className="list-badge list-badge--warn">{tr('dhcp.not_served', 'Sin definir')}</span>;
          return (
            <span className="list-badge list-badge--on" title={r.dhcp_ci_name ? tr('dhcp.cmdb_element', 'Elemento de la CMDB') : ''}>
              {served}
            </span>
          );
        },
      },
      {
        key: 'domain_name',
        label: tr('dhcp.domain', 'Dominio'),
        sortable: true,
        render: (r) => (r.domain_name ? <span className="list-code">{r.domain_name}</span> : dash),
      },
      {
        key: 'gateway',
        label: 'Gateway',
        sortable: true,
        render: (r) => (r.gateway ? <span className="list-code">{r.gateway}</span> : dash),
      },
      {
        key: 'dns',
        label: 'DNS',
        sortable: true,
        sortValue: (r) => chips(r.dns_servers).length,
        render: (r) => {
          const dns = chips(r.dns_servers);
          if (!dns.length) return dash;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {dns.map((o, i) => (
                <span key={i} className="list-code" title={o.ci_name || ''}>{o.ci_name || o.ip}</span>
              ))}
            </span>
          );
        },
      },
      {
        key: 'ntp',
        label: 'NTP',
        sortable: true,
        sortValue: (r) => chips(r.ntp_servers).length,
        render: (r) => {
          const ntp = chips(r.ntp_servers);
          if (!ntp.length) return dash;
          return (
            <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {ntp.map((o, i) => (
                <span key={i} className="list-code" title={o.ci_name || ''}>{o.ci_name || o.ip}</span>
              ))}
            </span>
          );
        },
      },
      {
        key: 'reservations',
        label: tr('dhcp.col_reservations', 'Reservas'),
        sortable: true,
        sortValue: (r) => r._reservations_count || 0,
        render: (r) => ((r._reservations_count || 0) > 0
          ? (
            <button
              type="button"
              className="list-badge list-badge--on"
              style={{ cursor: 'pointer' }}
              title={tr('dhcp.view_reservations', 'Ver las reservas de este rango')}
              onClick={(event) => { event.stopPropagation(); openDetail(r, { reservations: true }); }}
            >
              {r._reservations_count}
            </button>
          )
          : <span className="list-badge list-badge--off">0</span>),
      },
      {
        key: 'occupation',
        label: tr('ipam.occupation', 'Ocupación'),
        sortable: true,
        sortValue: (r) => occupationPct(r),
        render: (r) => {
          const pct = occupationPct(r);
          const tone = pct >= 90 ? 'var(--red-600)' : pct >= 70 ? 'var(--amber-600)' : 'var(--green-600)';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title={`${r._reservations_count || 0} reservas de ${r._range_size || 0} IPs`}>
              <span style={{ display: 'inline-block', width: 70, height: 7, borderRadius: 4, background: 'var(--bg-toolbar)', overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone }} />
              </span>
              <span className="list-hint" style={{ color: tone }}>{pct}%</span>
            </span>
          );
        },
      },
      {
        key: 'description',
        label: tr('description', 'Descripción'),
        sortable: true,
        render: (r) => r.description || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, siteOf]);

  const defaultVisible = ['range', 'size', 'subnet_cidr', 'site', 'served', 'domain_name', 'reservations', 'occupation'];

  const filters = [
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: knownSites.map(s => ({ value: s, label: s })),
    },
    {
      key: 'subnet', label: tr('ipam.subnet', 'Subred'), allLabel: tr('dhcp.subnet_all', 'Todas las subredes'),
      value: filterSubnet, onChange: setFilterSubnet,
      options: subnets.map(s => ({ value: String(s.id), label: s.cidr })),
    },
    {
      key: 'served', label: tr('dhcp.col_served', 'Lo sirve'), allLabel: tr('dhcp.served_all', 'Con y sin elemento'),
      value: filterServed, onChange: setFilterServed,
      options: [
        { value: 'yes', label: tr('dhcp.served_yes', 'Con elemento definido') },
        { value: 'no', label: tr('dhcp.served_no', 'Sin definir') },
      ],
    },
    {
      key: 'res', label: tr('dhcp.col_reservations', 'Reservas'), allLabel: tr('dhcp.res_all', 'Con y sin reservas'),
      value: filterRes, onChange: setFilterRes,
      options: [
        { value: 'with', label: tr('dhcp.res_with', 'Con reservas') },
        { value: 'without', label: tr('dhcp.res_without', 'Sin reservas') },
      ],
    },
    {
      key: 'occupation', label: tr('ipam.occupation', 'Ocupación'), allLabel: tr('ipam.occupation_all', 'Cualquier ocupación'),
      value: filterOccupation, onChange: setFilterOccupation,
      options: Object.entries(OCCUPATION_RANGES).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'domain', label: tr('dhcp.domain', 'Dominio'), allLabel: tr('dhcp.domain_all', 'Con y sin dominio'),
      value: filterDomain, onChange: setFilterDomain,
      options: [
        { value: 'yes', label: tr('dhcp.domain_yes', 'Con dominio') },
        { value: 'no', label: tr('dhcp.domain_no', 'Sin dominio') },
      ],
    },
  ];

  const subtitle = `${ranges.length} ${tr('dhcp.range_plural', 'rangos')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(r => ({
    'Rango': `${r.start_ip} – ${r.end_ip}`,
    'IPs': r._range_size || 0,
    'Subred': r.subnet_cidr || '',
    'Site': siteOf(r),
    'Lo sirve': r.dhcp_ci_name || r.owner || '',
    'Dominio': r.domain_name || '',
    'Gateway': r.gateway || '',
    'DNS': opcionesList(r.dns_servers).map(o => o.ci_name || o.ip).filter(Boolean).join(', '),
    'NTP': opcionesList(r.ntp_servers).map(o => o.ci_name || o.ip).filter(Boolean).join(', '),
    'Reservas': r._reservations_count || 0,
    'Ocupación (%)': occupationPct(r),
    'Descripción': r.description || '',
  })), [filtered, siteOf]);

  /** Tarjetas: lo esencial del rango, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((r) => {
          const pct = occupationPct(r);
          const tone = pct >= 90 ? 'var(--red-600)' : pct >= 70 ? 'var(--amber-600)' : 'var(--green-600)';
          const served = r.dhcp_ci_name || r.owner;
          const dns = opcionesList(r.dns_servers).filter(o => o.ip || o.ci_name);
          return (
            <div
              key={r.id}
              id={`dhcp-card-${r.id}`}
              className={`list-card${r.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openDetail(r)}
              onKeyDown={(event) => { if (event.key === 'Enter') openDetail(r); }}
              title={`${tr('action.view', 'Ver')} ${r.start_ip} – ${r.end_ip}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title list-code">{r.start_ip} – {r.end_ip}</div>
                  <span className="list-hint">{(r._range_size || 0).toLocaleString()} {tr('dhcp.ips', 'IPs')}</span>
                </div>
                <span className={`list-badge ${served ? 'list-badge--on' : 'list-badge--warn'}`}>
                  {served || tr('dhcp.not_served', 'Sin definir')}
                </span>
              </div>

              <div className="list-card-meta">
                {r.subnet_cidr && <span className="list-code">{r.subnet_cidr}</span>}
                {siteOf(r) && <span>📍 {siteOf(r)}</span>}
                {r.domain_name && <span className="list-code">{r.domain_name}</span>}
                {r.gateway && <span className="list-code">GW {r.gateway}</span>}
                {dns.length > 0 && <span className="list-hint">DNS {dns.length}</span>}
                <span className="list-badge">{r._reservations_count || 0} {tr('dhcp.reservations_lower', 'reservas')}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 90, height: 7, borderRadius: 4, background: 'var(--bg-toolbar)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone }} />
                </span>
                <span className="list-hint">{pct}%</span>
              </div>

              {r.description && <div className="list-card-meta"><span>{r.description}</span></div>}

              <div className="list-card-actions">{rowActions(r)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const DHCP_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada rango'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, siteOf]);
  useListView('ipam_dhcp', 'list');

  if (!companyId) {
    return (
      <IPAMLayout>
        <div className="alert alert-warning">{tr('ipam.select_company', 'Selecciona una empresa para ver el IPAM.')}</div>
      </IPAMLayout>
    );
  }

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between align-items-center mb-3">
          <span>{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between align-items-center mb-3">
          <span>{success}</span>
          <button className="btn-close btn-sm" onClick={() => setSuccess('')} />
        </div>
      )}

      {/* El filtro por subred llega desde Subredes («Ver rango DHCP» → ?subnetId=) */}
      {initSubnet && filterSubnet && (
        <div className="list-kpis list-kpis--chips" style={{ marginBottom: 12 }}>
          <div className="list-kpi-group">
            <span className="list-hint">{tr('dhcp.filtered_subnet_hint', 'Mostrando solo los rangos de la subred')}</span>
            <button
              type="button"
              className="list-kpi-chip list-kpi-chip--on"
              onClick={() => {
                setFilterSubnet('');
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('subnetId');
                  return next;
                }, { replace: true });
              }}
              title={tr('dhcp.filtered_subnet_clear', 'Quitar el filtro y ver todos los rangos')}
            >
              <span className="list-kpi-value">{filtered.length}</span>
              <span className="list-kpi-label">
                {(subnetById[String(filterSubnet)]?.cidr || `#${filterSubnet}`)} · {tr('dhcp.clear_filter', 'quitar filtro')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* KPIs del módulo */}
      <StatBar stats={stats} loading={loading} />

      <ListPage
        icon={ServerStackIcon}
        title={tr('dhcp.title', 'Rangos DHCP')}
        subtitle={subtitle}

        onCreate={openNewRange}
        createLabel={tr('dhcp.new_range', 'Nuevo rango')}
        createTitle={tr('dhcp.new_range_hint', 'Crear un scope: tramo de IPs y lo que reparte (dominio, gateway, DNS, NTP)')}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('dhcp.search', 'Buscar IP, subred, dominio, gateway, quién lo sirve…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="dhcp-rangos"
        exportSheetName="Rangos DHCP"

        columns={columns}
        columnStorageKey="cmdb_cols_ipam_dhcp"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(r) => r.id}
        rowId={(r) => `dhcp-row-${r.id}`}
        rowClassName={(r) => (r.id === hlId ? 'list-row-highlight' : '')}
        onRowClick={openDetail}
        rowTitle={(r) => `${tr('action.view', 'Ver')} ${r.start_ip} – ${r.end_ip}`}

        views={DHCP_VIEWS}
        listKey="ipam_dhcp"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando rangos DHCP…')}

        empty={{
          icon: '📡',
          title: ranges.length === 0
            ? tr('dhcp.empty', 'No hay rangos DHCP')
            : tr('msg.no_results', 'Ningún rango coincide con los filtros'),
          subtitle: ranges.length === 0
            ? tr('dhcp.empty_hint', 'Crea el primero: el tramo de IPs de una subred y lo que reparte a los clientes.')
            : undefined,
          action: ranges.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={openNewRange}>
              ＋ {tr('dhcp.new_range', 'Nuevo rango')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (r) => (
            <>
              {/* Ver reservas (ficha bajando a la tabla) */}
              <button
                type="button"
                className="list-action-btn"
                onClick={() => openDetail(r, { reservations: true })}
                title={tr('dhcp.view_reservations', 'Ver las reservas de este rango')}
              >
                <ServerStackIcon />
              </button>
              {/* Añadir reserva (ficha con el formulario abierto) */}
              <button
                type="button"
                className="list-action-btn"
                onClick={() => { openDetail(r); openNewRes(); }}
                title={tr('dhcp.add_reservation', 'Añadir una reserva a este rango')}
              >
                <PlusIcon />
              </button>
              {/* Ver la subred */}
              <button
                type="button"
                className="list-action-btn"
                disabled={!r.subnet_cidr}
                onClick={() => navigate(`/networks/subnets?cidr=${encodeURIComponent(r.subnet_cidr)}`)}
                title={tr('dhcp.view_subnet', 'Ver la subred del rango')}
              >
                <MagnifyingGlassIcon />
              </button>
              {/* Ver en el Explorer */}
              <button
                type="button"
                className="list-action-btn"
                disabled={!r.subnet_id}
                onClick={() => {
                  const subnet = subnetById[String(r.subnet_id)];
                  const params = new URLSearchParams();
                  if (subnet?.block_id != null) params.set('blockId', subnet.block_id);
                  params.set('subnetId', r.subnet_id);
                  navigate(`/networks/explorer?${params.toString()}`);
                }}
                title={tr('dhcp.view_in_explorer', 'Ver la subred en el Explorer')}
              >
                <Squares2X2Icon />
              </button>
            </>
          ),
          onCopy: openCopyRange,
          onEdit: openEditRange,
          onDelete: (r) => setConfirmDelete({ kind: 'range', item: r }),
          deleteTitle: tr('dhcp.delete_range_title', 'Eliminar rango DHCP'),
          deleteMessage: (r) => (
            <>
              {tr('dhcp.delete_range_confirm', '¿Seguro que quieres eliminar el rango')}{' '}
              <strong>{r.start_ip} – {r.end_ip}</strong>?{' '}
              {tr('dhcp.delete_range_hint', 'Se eliminarán también sus reservas')} ({r._reservations_count || 0}).{' '}
              {tr('ip.delete_warn', 'No se puede deshacer.')}
            </>
          ),
        }}
      />

      {/* ── Alta / edición del scope ──────────────────────────────────────── */}
      {showRangeForm && (
        <DhcpRangeForm
          companyId={companyId}
          subnets={subnets}
          editing={editingRange}
          form={rangeForm}
          onFormChange={setRangeForm}
          onDhcpCi={setDhcpCi}
          onAddIpOption={addIpOption}
          onAddCiOption={addCiOption}
          onRemoveOption={removeOption}
          onSave={handleSaveRange}
          saving={savingRange}
          onClose={() => { setShowRangeForm(false); setEditingRange(null); setRangeForm(EMPTY_RANGE_FORM); }}
        />
      )}

      {/* ── Ficha del rango con sus reservas ──────────────────────────────── */}
      {selectedRange && (
        <DhcpRangeDetail
          range={selectedRange}
          site={siteOf(selectedRange)}
          reservations={reservations}
          companyId={companyId}
          onClose={() => { setSelectedRange(null); setReservations([]); setShowResForm(false); setEditingRes(null); }}
          onEdit={() => openEditRange(selectedRange)}
          onDelete={() => setConfirmDelete({ kind: 'range', item: selectedRange })}
          showResForm={showResForm}
          resForm={resForm}
          onResFormChange={setResForm}
          editingRes={editingRes}
          onNewRes={openNewRes}
          onEditRes={openEditRes}
          onCancelRes={() => { setShowResForm(false); setEditingRes(null); }}
          onSaveRes={handleSaveRes}
          onDeleteRes={(res) => setConfirmDelete({ kind: 'res', item: res })}
          deviceOptions={deviceOptions}
          savingRes={savingRes}
        />
      )}

      {/* ── Confirmaciones: un diálogo del estándar ───────────────────────── */}
      {confirmDelete && (
        <Dialog
          title={confirmDelete.kind === 'range'
            ? tr('dhcp.delete_range_title', 'Eliminar rango DHCP')
            : tr('dhcp.delete_res_title', 'Eliminar reserva')}
          icon={<TrashIcon style={{ width: 18, height: 18 }} />}
          onClose={() => setConfirmDelete(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(null)}>
                {tr('action.cancel', 'Cancelar')}
              </button>
              <button
                type="button"
                className="list-btn-danger"
                onClick={() => (confirmDelete.kind === 'range'
                  ? handleDeleteRange(confirmDelete.item)
                  : handleDeleteRes(confirmDelete.item))}
              >
                {tr('action.confirm', 'Confirmar')}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            {confirmDelete.kind === 'range' ? (
              <>
                {tr('dhcp.delete_range_confirm', '¿Seguro que quieres eliminar el rango')}{' '}
                <strong>{confirmDelete.item.start_ip} – {confirmDelete.item.end_ip}</strong>?{' '}
                {tr('dhcp.delete_range_hint', 'Se eliminarán también sus reservas')} ({confirmDelete.item._reservations_count || 0}).{' '}
                {tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ) : (
              <>
                {tr('dhcp.delete_res_confirm', '¿Seguro que quieres eliminar la reserva de la MAC')}{' '}
                <strong>{confirmDelete.item.mac_address}</strong>
                {confirmDelete.item.ip ? <> ({confirmDelete.item.ip})</> : null}.{' '}
                {tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            )}
          </p>
        </Dialog>
      )}
    </IPAMLayout>
  );
}

/* ── Alta / edición del scope (rango + opciones que reparte) ─────────────────
   Modal del estándar. El mapa de IPs (`SubnetIpMapPicker`) sigue dentro: es la
   forma rápida de elegir el tramo viendo qué hay ya repartido.
────────────────────────────────────────────────────────────────────────────── */
function DhcpRangeForm({
  companyId, subnets, editing, form, onFormChange, onDhcpCi,
  onAddIpOption, onAddCiOption, onRemoveOption, onSave, saving, onClose,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const sOk = isValidIP(form.start_ip);
  const eOk = isValidIP(form.end_ip);
  const both = sOk && eOk;
  const sz = both ? rangeSize(form.start_ip, form.end_ip) : null;
  const orderOk = sz !== null && sz > 0;
  const selSubnet = subnets.find(s => String(s.id) === String(form.subnet_id));
  const subnetPrefix = selSubnet?.cidr?.split('/')[0]?.split('.').slice(0, 3).join('.');
  const startInSubnet = sOk && subnetPrefix ? form.start_ip.startsWith(`${subnetPrefix}.`) : true;
  const endInSubnet = eOk && subnetPrefix ? form.end_ip.startsWith(`${subnetPrefix}.`) : true;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header py-3">
            <h5 className="modal-title fw-bold">
              {editing
                ? `${tr('dhcp.edit_range', 'Editar rango DHCP')} · ${editing.start_ip} – ${editing.end_ip}`
                : tr('dhcp.new_range', 'Nuevo rango DHCP')}
            </h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label small mb-1">{tr('ipam.subnet', 'Subred')}</label>
              <select
                className="form-select form-select-sm"
                value={form.subnet_id}
                onChange={e => onFormChange(f => ({ ...f, subnet_id: e.target.value }))}
              >
                <option value="">{tr('dhcp.no_subnet', 'Sin subred')}</option>
                {subnets.map(s => (
                  <option key={s.id} value={s.id}>{s.cidr}{s.name ? ` · ${s.name}` : ''}</option>
                ))}
              </select>
            </div>

            {form.subnet_id && (
              <SubnetIpMapPicker
                companyId={companyId}
                subnetId={form.subnet_id}
                startIp={form.start_ip}
                endIp={form.end_ip}
                onStartChange={v => onFormChange(f => ({ ...f, start_ip: v }))}
                onEndChange={v => onFormChange(f => ({ ...f, end_ip: v }))}
              />
            )}

            {/* Resumen del tramo: se colorea según lo que hay elegido */}
            <div
              className="list-kpi-group"
              style={{
                border: `1px solid ${both && orderOk ? 'var(--border)' : both ? 'var(--red-600)' : 'var(--border)'}`,
                borderRadius: 8, padding: '6px 10px', marginBottom: 8, alignItems: 'center',
              }}
            >
              <span className="list-code" style={{ minWidth: 105 }}>{sOk ? form.start_ip : '···.···.···.···'}</span>
              <span className="list-hint">→</span>
              <span className="list-code" style={{ minWidth: 105 }}>{eOk ? form.end_ip : '···.···.···.···'}</span>
              <span className="list-badge" style={{ marginLeft: 'auto' }}>
                {both && orderOk
                  ? `${sz.toLocaleString()} IPs`
                  : both ? tr('dhcp.end_before_start', 'fin < inicio') : tr('dhcp.enter_ips', 'introduce IPs')}
              </span>
            </div>

            <div className="row g-2 mb-2">
              <div className="col">
                <label className="form-label small mb-1">{tr('dhcp.start_ip', 'IP inicio')} *</label>
                <input
                  className={`form-control form-control-sm${form.start_ip ? (sOk && startInSubnet ? ' is-valid' : ' is-invalid') : ''}`}
                  placeholder="192.168.1.100"
                  value={form.start_ip}
                  onChange={e => onFormChange(f => ({ ...f, start_ip: e.target.value }))}
                />
                {form.start_ip && !sOk && <div className="invalid-feedback">{tr('dhcp.bad_ip', 'IP inválida')}</div>}
                {sOk && !startInSubnet && <div className="invalid-feedback">{tr('dhcp.out_of_subnet', 'Fuera de la subred seleccionada')}</div>}
              </div>
              <div className="col">
                <label className="form-label small mb-1">{tr('dhcp.end_ip', 'IP fin')} *</label>
                <input
                  className={`form-control form-control-sm${form.end_ip ? (eOk && endInSubnet && orderOk ? ' is-valid' : ' is-invalid') : ''}`}
                  placeholder="192.168.1.200"
                  value={form.end_ip}
                  onChange={e => onFormChange(f => ({ ...f, end_ip: e.target.value }))}
                />
                {form.end_ip && !eOk && <div className="invalid-feedback">{tr('dhcp.bad_ip', 'IP inválida')}</div>}
                {eOk && !endInSubnet && <div className="invalid-feedback">{tr('dhcp.out_of_subnet', 'Fuera de la subred seleccionada')}</div>}
                {eOk && !orderOk && endInSubnet && <div className="invalid-feedback">{tr('dhcp.must_be_higher', 'Debe ser mayor que IP inicio')}</div>}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small mb-1">{tr('description', 'Descripción')}</label>
              <input
                className="form-control form-control-sm"
                placeholder={tr('dhcp.description_ph', 'Descripción (opcional)')}
                value={form.description}
                onChange={e => onFormChange(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* ── Opciones del scope ─────────────────────────────────────── */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
              <div className="list-hint" style={{ fontWeight: 700, marginBottom: 6 }}>
                {tr('dhcp.scope_options', 'Opciones del scope · qué reparte el DHCP')}
              </div>

              <label className="form-label small fw-semibold mb-1">
                {tr('dhcp.served_by', 'Lo sirve (elemento de la CMDB)')}
              </label>
              {form.dhcp_ci_id ? (
                <div style={{ marginBottom: 6 }}>
                  <CiChip
                    entrada={{ ci_type: form.dhcp_ci_type, ci_id: form.dhcp_ci_id, ci_name: form.owner }}
                    onRemove={() => onFormChange(f => ({ ...f, dhcp_ci_type: '', dhcp_ci_id: '' }))}
                  />
                </div>
              ) : (
                <div className="mb-2">
                  <CiPicker
                    companyId={companyId}
                    placeholder={tr('dhcp.served_by_ph', 'Buscar el servidor o firewall que sirve este DHCP…')}
                    onPick={onDhcpCi}
                  />
                </div>
              )}

              <div className="row g-2 mb-2">
                <div className="col">
                  <label className="form-label small mb-1">Domain Name</label>
                  <input className="form-control form-control-sm" placeholder="adder.local"
                    value={form.domain_name}
                    onChange={e => onFormChange(f => ({ ...f, domain_name: e.target.value }))} />
                </div>
                <div className="col">
                  <label className="form-label small mb-1">Gateway</label>
                  <input className="form-control form-control-sm" placeholder="192.168.1.1"
                    value={form.gateway}
                    onChange={e => onFormChange(f => ({ ...f, gateway: e.target.value }))} />
                </div>
              </div>

              {[['dns_servers', 'DNS', '10.60.1.10, 10.60.1.11', tr('dhcp.find_dns', 'Buscar el servidor DNS en la CMDB…')],
                ['ntp_servers', 'NTP', '10.60.1.20', tr('dhcp.find_ntp', 'Buscar el servidor NTP en la CMDB…')]].map(([campo, etiqueta, ph, buscar]) => (
                <div className="mb-2" key={campo}>
                  <label className="form-label small fw-semibold mb-1">{etiqueta}</label>
                  <div style={{ marginBottom: 4 }}>
                    {opcionesList(form[campo]).map((o, i) => (
                      <CiChip key={`${campo}-${i}`} entrada={o} onRemove={() => onRemoveOption(campo, i)} />
                    ))}
                  </div>
                  <input
                    className="form-control form-control-sm"
                    placeholder={`${tr('dhcp.ips_comma', 'IPs separadas por comas')} — ${ph}`}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); onAddIpOption(campo, e.target.value); e.target.value = ''; }
                    }}
                    onBlur={e => { onAddIpOption(campo, e.target.value); e.target.value = ''; }}
                  />
                  <div style={{ marginTop: 4 }}>
                    <CiPicker companyId={companyId} placeholder={buscar} onPick={it => onAddCiOption(campo, it)} />
                  </div>
                </div>
              ))}
              <div className="list-hint">
                {tr('dhcp.link_hint', 'Si enlazas el elemento, el diseñador de servicios pinta solo la dependencia (quién sirve el DHCP y a quién le da DNS/NTP).')}
              </div>
            </div>
          </div>

          <div className="modal-footer py-2">
            <button type="button" className="list-btn-secondary" onClick={onClose}>
              {tr('action.cancel', 'Cancelar')}
            </button>
            <button
              type="button"
              className="list-btn-primary"
              onClick={onSave}
              disabled={saving || !isValidIP(form.start_ip) || !isValidIP(form.end_ip) || (rangeSize(form.start_ip, form.end_ip) ?? 0) <= 0}
            >
              {saving ? tr('msg.saving', 'Guardando…') : (editing ? tr('action.save', 'Guardar cambios') : tr('dhcp.save_range', 'Guardar rango'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Ficha del rango (lectura) + sus reservas ────────────────────────────────
   Los datos del scope se leen aquí (para cambiarlos, «Editar» abre el formulario)
   y la tabla de reservas se gestiona dentro, que es donde tiene sentido: alta,
   edición y borrado de la MAC → IP.
────────────────────────────────────────────────────────────────────────────── */
function DhcpRangeDetail({
  range, site, reservations, companyId, onClose, onEdit, onDelete,
  showResForm, resForm, onResFormChange, editingRes, onNewRes, onEditRes, onCancelRes,
  onSaveRes, onDeleteRes, deviceOptions, savingRes,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const dns = opcionesList(range.dns_servers).filter(o => o.ip || o.ci_name);
  const ntp = opcionesList(range.ntp_servers).filter(o => o.ip || o.ci_name);
  const served = range.dhcp_ci_name || range.owner;
  const size = range._range_size || 0;
  const used = range._reservations_count || 0;
  const pct = size ? Math.min(100, Math.round((used / size) * 100)) : 0;
  const tone = pct >= 90 ? 'var(--red-600)' : pct >= 70 ? 'var(--amber-600)' : 'var(--green-600)';

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header py-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="list-badge list-badge--on">DHCP</span>
                {served
                  ? <span className="list-badge">{served}</span>
                  : <span className="list-badge list-badge--warn">{tr('dhcp.not_served', 'Sin definir')}</span>}
              </div>
              <h5 className="modal-title fw-bold list-code">
                {range.start_ip} – {range.end_ip}
              </h5>
              {range.description && <span className="list-hint">{range.description}</span>}
            </div>
            <div className="d-flex gap-2 ms-auto me-2">
              <button type="button" className="list-btn-secondary" onClick={onEdit}>
                <PencilSquareIcon style={{ width: 15, height: 15 }} /> {tr('action.edit', 'Editar')}
              </button>
              <button type="button" className="list-btn-secondary" onClick={onDelete}>
                <TrashIcon style={{ width: 15, height: 15 }} /> {tr('action.delete', 'Eliminar')}
              </button>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* ── Datos del scope, en lectura ───────────────────────────── */}
            <div className="list-kpi-group" style={{ gap: '1.5rem', marginBottom: '0.75rem' }}>
              <div>
                <div className="list-hint">{tr('ipam.subnet', 'Subred')}</div>
                <div className="list-code">{range.subnet_cidr || '—'}</div>
              </div>
              <div>
                <div className="list-hint">{tr('ipam.site', 'Site')}</div>
                <div>{site || '—'}</div>
              </div>
              <div>
                <div className="list-hint">{tr('dhcp.col_size', 'Tamaño')}</div>
                <div>{size.toLocaleString()} IPs</div>
              </div>
              <div>
                <div className="list-hint">Gateway</div>
                <div className="list-code">{range.gateway || '—'}</div>
              </div>
              <div>
                <div className="list-hint">Domain Name</div>
                <div className="list-code">{range.domain_name || '—'}</div>
              </div>
              <div>
                <div className="list-hint">{tr('ipam.occupation', 'Ocupación')}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 70, height: 7, borderRadius: 4, background: 'var(--bg-toolbar)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: tone }} />
                  </span>
                  <span className="list-hint" style={{ color: tone }}>{pct}% · {used} {tr('dhcp.reservations_lower', 'reservas')}</span>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="list-hint" style={{ marginBottom: 4 }}>
                {tr('dhcp.dns_ntp', 'DNS / NTP que reparte')}
              </div>
              <div>
                {dns.length === 0 && ntp.length === 0 && <span className="list-hint">{tr('dhcp.no_options', 'Sin DNS ni NTP configurados.')}</span>}
                {dns.map((o, i) => <CiChip key={`d-${i}`} entrada={o} />)}
                {ntp.map((o, i) => <CiChip key={`n-${i}`} entrada={o} />)}
              </div>
            </div>

            {/* ── Reservas ──────────────────────────────────────────────── */}
            <div id="dhcp-reservations" className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0 fw-semibold">
                {tr('dhcp.reservations', 'Reservas')}
                <span className="list-badge ms-2">{reservations.length}</span>
              </h6>
              {!showResForm && (
                <button type="button" className="list-btn-primary" onClick={onNewRes}>
                  <PlusIcon style={{ width: 14, height: 14 }} /> {tr('dhcp.add_reservation', 'Añadir reserva')}
                </button>
              )}
            </div>

            {showResForm && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div className="list-hint" style={{ fontWeight: 700, marginBottom: 8 }}>
                  {editingRes ? tr('dhcp.edit_res', 'Editar reserva') : tr('dhcp.new_res', 'Nueva reserva')}
                </div>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">MAC address *</label>
                    <input className="form-control form-control-sm list-code" placeholder="aa:bb:cc:dd:ee:ff"
                      value={resForm.mac_address}
                      onChange={e => onResFormChange(f => ({ ...f, mac_address: normMac(e.target.value) }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold">{tr('dhcp.reserved_ip', 'IP reservada')}</label>
                    <input className="form-control form-control-sm list-code"
                      placeholder={`${range.start_ip}…`}
                      value={resForm.ip}
                      onChange={e => onResFormChange(f => ({ ...f, ip: e.target.value }))} />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-semibold">Hostname</label>
                    <input className="form-control form-control-sm" placeholder="pc-recepcion"
                      value={resForm.hostname}
                      onChange={e => onResFormChange(f => ({ ...f, hostname: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">{tr('dhcp.device_type', 'Tipo dispositivo')}</label>
                    <select className="form-select form-select-sm"
                      value={resForm.object_type}
                      onChange={e => onResFormChange(f => ({ ...f, object_type: e.target.value, object_id: '' }))}>
                      <option value="">{tr('dhcp.none', 'Ninguno')}</option>
                      {Object.entries(OBJECT_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  {resForm.object_type && resForm.object_type !== 'other' && (
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">{tr('dhcp.device', 'Dispositivo')}</label>
                      <select className="form-select form-select-sm"
                        value={resForm.object_id}
                        onChange={e => onResFormChange(f => ({ ...f, object_id: e.target.value }))}>
                        <option value="">{tr('dhcp.select', 'Seleccionar')}</option>
                        {deviceOptions.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.name || d.code || d.hostname || `#${d.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">{tr('description', 'Descripción')}</label>
                    <input className="form-control form-control-sm" placeholder={tr('dhcp.res_desc_ph', 'PC Recepción planta 1')}
                      value={resForm.description}
                      onChange={e => onResFormChange(f => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
                <div className="d-flex gap-2" style={{ marginTop: 10 }}>
                  <button type="button" className="list-btn-primary" onClick={onSaveRes} disabled={savingRes}>
                    {savingRes ? tr('msg.saving', 'Guardando…') : (editingRes ? tr('action.save', 'Actualizar') : tr('dhcp.create_res', 'Crear reserva'))}
                  </button>
                  <button type="button" className="list-btn-secondary" onClick={onCancelRes}>
                    {tr('action.cancel', 'Cancelar')}
                  </button>
                </div>
              </div>
            )}

            {reservations.length === 0 ? (
              <div className="list-empty" style={{ padding: '1.5rem' }}>
                <ServerStackIcon style={{ width: 36, height: 36, opacity: 0.3 }} />
                <p className="list-hint" style={{ marginTop: '0.5rem' }}>
                  {tr('dhcp.res_empty', 'Sin reservas en este rango: usa «Añadir reserva» para fijar una MAC a una IP.')}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>MAC address</th>
                      <th>{tr('dhcp.reserved_ip', 'IP reservada')}</th>
                      <th>Hostname</th>
                      <th>{tr('dhcp.device', 'Dispositivo')}</th>
                      <th>{tr('description', 'Descripción')}</th>
                      <th className="text-end" style={{ width: 90 }}>{tr('list.actions', 'Acciones')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.map(res => (
                      <tr key={res.id}>
                        <td><span className="list-code" style={{ fontWeight: 600 }}>{res.mac_address}</span></td>
                        <td>
                          {res.ip
                            ? <span className="list-code">{res.ip}</span>
                            : <span className="list-hint">{tr('dhcp.dynamic', 'Dinámica')}</span>}
                        </td>
                        <td>{res.hostname || <span className="list-hint">—</span>}</td>
                        <td>
                          {res.object_type
                            ? (
                              <span className="list-badge">
                                {OBJECT_LABELS[res.object_type] || res.object_type}
                                {res.object_id ? ` #${res.object_id}` : ''}
                              </span>
                            )
                            : <span className="list-hint">—</span>}
                        </td>
                        <td>{res.description || <span className="list-hint">—</span>}</td>
                        <td className="text-end">
                          <div style={{ display: 'inline-flex', gap: 4 }}>
                            <button
                              type="button"
                              className="list-action-btn"
                              onClick={() => onEditRes(res)}
                              title={tr('action.edit', 'Editar')}
                            >
                              <PencilSquareIcon />
                            </button>
                            <button
                              type="button"
                              className="list-action-btn list-action-btn--danger"
                              onClick={() => onDeleteRes(res)}
                              title={tr('dhcp.remove_res', 'Quitar reserva')}
                            >
                              <XMarkIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-footer py-2">
            <button type="button" className="list-btn-secondary" onClick={onClose}>
              {tr('action.close', 'Cerrar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


